/**
 * CZNA-530 — say a drift item in Czech.
 *
 * The ticket's §4: not `defaultVisible: false → true` but "Výchozí viditelnost:
 * ne → ano". Kept out of the component and parameterised on a plain
 * `translate` function so the phrasing is asserted directly, without rendering a
 * modal to find out whether a boolean came out as "ano" or as "true".
 */
import { DRIFT_ATTRIBUTE_MESSAGE_IDS } from './formConfigDriftItems';
var M = function (suffix) { return "ui-czna-authorities.settings.forms.drift.".concat(suffix); };
/**
 * Long enough for a `visibleWhen` or a two-option `selectOptions` to read as
 * itself, short enough not to reintroduce the wall of JSON. The full value is one
 * click away in the raw-JSON details.
 */
var MAX_INLINE_JSON = 120;
/**
 * One attribute value as prose.
 *
 * `undefined` and `null` both render as "neuvedeno": at render time an absent
 * attribute and an explicit `null` behave identically, so distinguishing them
 * here would be a distinction without a form-level difference.
 */
export var formatDriftValue = function (t, value) {
    if (value === undefined || value === null)
        return t(M('value.absent'));
    if (typeof value === 'boolean')
        return t(M(value ? 'value.true' : 'value.false'));
    if (typeof value === 'number')
        return String(value);
    if (typeof value === 'string') {
        if (value === '')
            return t(M('value.empty'));
        return "\u201E".concat(value, "\u201C");
    }
    var json = JSON.stringify(value);
    return json.length > MAX_INLINE_JSON ? "".concat(json.slice(0, MAX_INLINE_JSON), "\u2026") : json;
};
/**
 * The attribute's Czech name, or its raw JSON name when we have none.
 *
 * The fallback is deliberately visible rather than tidy: an unnamed attribute is a
 * template attribute nobody has translated yet, and showing `allowFreeText` is
 * better than dropping the line.
 */
export var attributeName = function (t, attribute) {
    var messageId = DRIFT_ATTRIBUTE_MESSAGE_IDS[attribute];
    return messageId === undefined ? attribute : t(messageId);
};
/** "podpole $b" / "vlastnosti pole" — the heading of one group of items. */
export var groupTitle = function (t, group) { return (group.subfieldCode === null
    ? t(M('item.fieldItself'))
    : t(M('item.subfield'), { code: group.subfieldCode })); };
/**
 * CZNA-1527 — a list of subfield codes reads as `$f $o`, the way the form names
 * subfields, rather than as `["f","o"]`. Anything else falls back to the generic form.
 */
var formatAttributeValue = function (t, attribute, value) {
    if (attribute === 'offerExcludedSubfields' && Array.isArray(value) && value.length > 0
        && value.every(function (code) { return typeof code === 'string'; })) {
        return value.map(function (code) { return "$".concat(code); }).join(' ');
    }
    return formatDriftValue(t, value);
};
/** One readable line for one item. */
export var describeItem = function (t, item) {
    var _a;
    switch (item.kind) {
        case 'SUBFIELD_ADDED':
            return t(M('item.subfieldAdded'), { code: item.subfieldCode });
        case 'SUBFIELD_REMOVED':
            return t(M('item.subfieldRemoved'), { code: item.subfieldCode });
        case 'WHOLE_ADDED':
            return t(M('item.wholeAdded'));
        case 'WHOLE_REMOVED':
            return t(M('item.wholeRemoved'));
        case 'WHOLE_CHANGED':
            return t(M('item.wholeChanged'));
        default:
            return "".concat(attributeName(t, (_a = item.attribute) !== null && _a !== void 0 ? _a : ''), ": ").concat(t(M('value.arrow'), {
                from: formatAttributeValue(t, item.attribute, item.local),
                to: formatAttributeValue(t, item.attribute, item.packagedDefault),
            }));
    }
};
/**
 * The distinct reasons behind a set of technical items, as one clause list.
 *
 * Named rather than counted: "6 technických rozdílů" alone invites the suspicion
 * that something real is being swept up, which is the objection the ticket's
 * "Pozor" section raises. Saying WHY each one is inert is what makes the
 * collapsed section trustworthy enough to stay collapsed.
 */
export var technicalReasonsClause = function (t, items) {
    var seen = [];
    items.forEach(function (item) {
        if (item.technicalReason !== null && !seen.includes(item.technicalReason)) {
            seen.push(item.technicalReason);
        }
    });
    return seen.map(function (reason) { return t(M("summary.reason.".concat(reason))); }).join('; ');
};
